import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { PageHeader, useTitle } from "@/components/PageHeader";
import { Reveal, Overline } from "@/components/Reveal";
import { api, asset } from "@/lib/api";
import { IMAGES } from "@/data/content";

const GUIDE_FALLBACK = [IMAGES.manifesto, IMAGES.ridge, IMAGES.town, IMAGES.aurora];

// Turn any YouTube / Vimeo watch link into an embeddable URL. Returns null if not embeddable.
const embedUrl = (raw) => {
  const url = (raw || "").trim();
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
};

const isFileVideo = (url) => /\.(mp4|webm|ogg)(\?|$)/i.test(url || "") || (url || "").startsWith("/api");

const Block = ({ block, i }) => {
  if (block.type === "image" && block.url) {
    return (
      <Reveal delay={i * 0.04}>
        <figure className="my-2" data-testid={`guide-block-image-${i}`}>
          <img src={asset(block.url)} alt={block.caption || ""} className="w-full max-h-[560px] object-cover border border-[#2A201A]" />
          {block.caption && <figcaption className="mt-3 font-mono text-xs text-[#8F877C]">{block.caption}</figcaption>}
        </figure>
      </Reveal>
    );
  }
  if (block.type === "video" && block.url) {
    const embed = embedUrl(block.url);
    return (
      <Reveal delay={i * 0.04}>
        <div className="my-2" data-testid={`guide-block-video-${i}`}>
          {embed ? (
            <div className="relative w-full border border-[#2A201A]" style={{ aspectRatio: "16 / 9" }}>
              <iframe src={embed} title={block.caption || `video-${i}`} className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          ) : isFileVideo(block.url) ? (
            <video src={asset(block.url)} controls className="w-full border border-[#2A201A]" />
          ) : (
            <a href={block.url} target="_blank" rel="noreferrer" className="text-teal underline">{block.url}</a>
          )}
          {block.caption && <p className="mt-3 font-mono text-xs text-[#8F877C]">{block.caption}</p>}
        </div>
      </Reveal>
    );
  }
  // text
  return (
    <Reveal delay={i * 0.04}>
      <div data-testid={`guide-block-text-${i}`}>
        {block.heading && <h2 className="font-display text-3xl font-light text-bone">{block.heading}</h2>}
        {block.body && <p className="mt-4 text-[#b8b0a4] leading-relaxed whitespace-pre-line">{block.body}</p>}
      </div>
    </Reveal>
  );
};

export default function GuideDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/guides").then(({ data }) => setGuides(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const idx = guides.findIndex((g) => (g.slug || g.id) === slug);
  const guide = guides[idx];
  useTitle(guide ? `${guide.title} | Aurora Springs RP Guide` : "Player Guide | Aurora Springs RP | RedM Roleplay", guide && guide.blurb ? guide.blurb : "Player guide articles from Aurora Springs RP, an 18+ RedM Wild West roleplay server.");

  if (loading) return <div className="pt-40 pb-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;
  if (!guide) {
    return (
      <div className="pt-40 pb-32 text-center">
        <p className="font-display text-4xl">Guide not found.</p>
        <Link to="/guide" data-testid="guide-notfound-back" className="mt-6 inline-block font-feature text-xs uppercase tracking-widest text-teal">← Back to Guides</Link>
      </div>
    );
  }
  const cover = guide.cover ? asset(guide.cover) : GUIDE_FALLBACK[(idx < 0 ? 0 : idx) % GUIDE_FALLBACK.length];
  const others = guides.filter((g) => (g.slug || g.id) !== slug).slice(0, 3);

  return (
    <>
      <PageHeader overline="Player Guide" title={guide.title} image={cover} sub={guide.blurb} />
      <section className="mx-auto max-w-3xl px-6 sm:px-10 py-16">
        <button onClick={() => navigate("/guide")} data-testid="guide-back-btn"
          className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest text-[#8F877C] hover:text-teal transition-colors mb-12">
          <ArrowLeft className="h-4 w-4" /> All Guides
        </button>
        <article className="space-y-10" data-testid="guide-article">
          {(guide.blocks || []).map((b, i) => <Block key={i} block={b} i={i} />)}
        </article>
      </section>

      {others.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 sm:px-10 pb-24">
          <Overline>More guides</Overline>
          <div className="mt-8 grid gap-6 sm:grid-cols-3">
            {others.map((o) => (
              <Link key={o.id} to={`/guide/${o.slug || o.id}`} data-testid={`guide-more-${o.slug || o.id}`}
                className="group border border-[#2A201A] bg-surface/30 p-6 hover:border-teal/40 transition-colors">
                <span className="font-mono text-[10px] uppercase tracking-widest text-teal">Guide</span>
                <h3 className="mt-2 font-display text-2xl font-light group-hover:text-teal transition-colors">{o.title}</h3>
              </Link>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
